import { Badge, createDataTableColumnHelper, DataTable, Link as UiLink } from "@beweegbaas/ui";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod/v4";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import {
  getStudentBeweegHistorie,
  submitClearBeweegresultaat,
} from "#/modules/school.students/students.functions";
import { HistorieRowActions } from "#/modules/student/components/historie-row-actions";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import {
  OverachievedIconButton,
  ScoreStatusIndicatorLabel,
} from "#/modules/util.score-status/components/score-status";
import { getScoreStatus } from "#/modules/util.score-status/score-status";
import type { components } from "#/schema";
import { apiEnum } from "#/modules/util.helpers/zod-enum";
import { LeerjaarCombo } from "#/schema.nswag";

// Child-own params, carried by the parent table's name-column link search updater (see
// `results/route.tsx`) — not part of the parent's own
// `beweegresultatenSearchSchema`. No defaults: absence is a genuine error case (see loader below),
// exactly like the old `sp.get(...)` + throw.
const historieSearchSchema = z.object({
  beweegactiviteitenReeksId: z.string().optional(),
  leerjaarCombo: apiEnum(LeerjaarCombo).optional(),
});

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/students/$id/results/drawer/historie/$beweegactiviteitId",
)({
  validateSearch: historieSearchSchema,
  loaderDeps: ({ search }) => ({
    beweegactiviteitenReeksId: search.beweegactiviteitenReeksId,
    leerjaarCombo: search.leerjaarCombo,
  }),
  loader: ({ deps, params }) => {
    const { beweegactiviteitenReeksId, leerjaarCombo } = deps;
    if (!beweegactiviteitenReeksId || !leerjaarCombo) {
      throw new Error("Missing required parameters");
    }
    return getStudentBeweegHistorie({
      data: { leerlingId: params.id, beweegactiviteitenReeksId, leerjaarCombo },
    });
  },
  component: BeweegresultatenHistorie,
  errorComponent: ({ error }) => <ErrorBoundaryComponent error={error} />,
});

function formatResultaat(resultaat: components["schemas"]["BeweegresultaatDocentModel"]) {
  if (resultaat.behaaldNiveau != null && resultaat.maxNiveau != null) {
    return `${resultaat.behaaldNiveau}/${resultaat.maxNiveau}`;
  }
  return "-";
}

function formatDate(date: Date | string | null | undefined) {
  if (!date) return "Onbekend";
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
}

type HistorieRow = NonNullable<
  Awaited<ReturnType<typeof getStudentBeweegHistorie>>["historie"]
>["beweegresultaten"][number];

const helper = createDataTableColumnHelper<HistorieRow>();

function BeweegHistorieActions({ row }: { row: HistorieRow }) {
  const { id: leerlingId } = Route.useParams();
  const clearBeweegresultaat = useServerFn(submitClearBeweegresultaat);
  const { beweegresultaatId, lesId, behaaldNiveau } = row;

  return (
    <HistorieRowActions
      lesId={lesId}
      clearDisabled={!beweegresultaatId || behaaldNiveau == null}
      onClear={async () => {
        await clearBeweegresultaat({
          data: { leerlingId, beweegresultaatId: beweegresultaatId! },
        });
      }}
    />
  );
}

const columns = helper.columns([
  helper.display({
    id: "resultaat",
    header: () => m["common.resultaat"](),
    meta: { cellClassName: "py-3" },
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        <ScoreStatusIndicatorLabel
          status={getScoreStatus(row.original.behaaldNiveau ?? 0, row.original.maxNiveau ?? 0)}
          label={formatResultaat(row.original)}
        />

        {row.original.isOverachieved && <OverachievedIconButton />}
      </div>
    ),
  }),
  helper.display({
    id: "datum",
    header: () => m["common.fields.date"](),
    meta: { rowTarget: true, cellClassName: "py-3 text-sm" },
    cell: ({ row }) => (
      <UiLink
        variant="secondary"
        underline
        to="/$locale/{-$schoolyear}/$school/lesson/$lesson/teacher/scores"
        params={(prev: Record<string, string>) => ({ ...prev, lesson: row.original.lesId! })}
      >
        <Badge variant="gray" size="sm">
          {formatDate(row.original.behaaldChangedAt)}
        </Badge>
      </UiLink>
    ),
  }),
  helper.display({
    id: "actions",
    header: "",
    meta: { action: true, headerClassName: "w-0" },
    cell: ({ row }) => <BeweegHistorieActions row={row.original} />,
  }),
]);

function BeweegresultatenHistorie() {
  const { historie } = Route.useLoaderData();

  if (!historie) {
    return (
      <div className="px-10 pt-10 pb-10">
        <p className="text-gray-500">Geen historie gevonden.</p>
      </div>
    );
  }

  return (
    <section className="pt-10">
      <h3 className="m-0 mb-2 px-4 pr-10">{m["student.results.historie.title"]()}</h3>
      <p className="text-muted-foreground mb-4 px-4 text-sm">{historie.beweegactiviteitName}</p>
      {historie.beweegresultaten.length === 0 ? (
        <p className="text-muted-foreground px-4 text-sm">
          {m["student.results.historie.no_results"]()}
        </p>
      ) : (
        <DataTable
          className="-mr-6"
          aria-label={m["student.results.historie.title"]()}
          columns={columns}
          data={historie.beweegresultaten}
        />
      )}
    </section>
  );
}
